import { useMemo, useState } from 'react';

import {
  buildFiscalYearRange,
  describeFiscalPeriodConfig,
  matchingPresetId,
  weekdayName,
  weeksForPattern,
  yearEndWeekday,
  type FiscalPeriodConfig,
} from 'utils/fiscalPeriods';

import { AnchorRuleFields } from './fiscalPeriodsEditor/AnchorRuleFields';
import { DefinedByChoice } from './fiscalPeriodsEditor/DefinedByChoice';
import { ExtraWeekField } from './fiscalPeriodsEditor/ExtraWeekField';
import { FiscalPreviewSection } from './fiscalPeriodsEditor/FiscalPreviewSection';
import { PatternPicker } from './fiscalPeriodsEditor/PatternPicker';
import { patchFiscalPeriodConfig } from './fiscalPeriodsEditor/patchFiscalPeriodConfig';
import { PresetSelector } from './fiscalPeriodsEditor/PresetSelector';
import { SummaryBox } from './fiscalPeriodsEditor/SummaryBox';
import { WeekdayPicker } from './fiscalPeriodsEditor/WeekdayPicker';

import './FiscalPeriodsEditor.scss';

/**
 * Interactive editor for a global fiscal-period calendar config.
 *
 * Renders pattern / weekday / anchor / defined-by controls plus a multi-year
 * period preview. `config` is the normalized value to display; every control
 * calls `onChange` with a full next {@link FiscalPeriodConfig} (never a partial).
 */
const FiscalPeriodsEditor: React.FC<{
  config: FiscalPeriodConfig;
  onChange: (next: FiscalPeriodConfig) => void;
}> = ({ config, onChange }) => {
  const thisYear = new Date().getUTCFullYear();
  const [previewYear, setPreviewYear] = useState(thisYear);

  const patch = (partial: Partial<FiscalPeriodConfig>) => {
    onChange(patchFiscalPeriodConfig(config, partial));
  };

  // Sliding window around the current year (not a fixed 2024–2028 floor/ceiling).
  const fromYear = thisYear - 2;
  const toYear = thisYear + 3;
  const years = useMemo(() => buildFiscalYearRange(config, fromYear, toYear), [config, fromYear, toYear]);
  const selectedYear = years.find((y) => y.year === previewYear) ?? years[0];
  const summary = describeFiscalPeriodConfig(config);
  const presetId = matchingPresetId(config) ?? '';
  const endDay = weekdayName(yearEndWeekday(config.weekday));
  const startDay = weekdayName(config.weekday);

  return (
    <div className="fiscal-period-editor mb-2">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h5 className="mb-1">Fiscal Period Calendar</h5>
          <div className="text-muted">
            A week-based year: every period is normally 4 or 5 weeks (period 12 can be 6 in a 53-week 4-4-5 year), a quarter is 13 weeks, and a standard year is
            52 weeks. Period 1 always starts on a fixed weekday — never on a fixed calendar date.
          </div>
        </div>
      </div>

      <PresetSelector presetId={presetId} onChange={onChange} />
      <PatternPicker pattern={config.pattern} patch={patch} />
      <WeekdayPicker weekday={config.weekday} startDay={startDay} endDay={endDay} patch={patch} />
      <AnchorRuleFields config={config} patch={patch} />
      <DefinedByChoice config={config} startDay={startDay} endDay={endDay} patch={patch} />
      <ExtraWeekField extraWeek={config.extra_week} />
      <SummaryBox summary={summary} />
      <FiscalPreviewSection
        config={config}
        years={years}
        selectedYear={selectedYear}
        patternLabel={weeksForPattern(config.pattern).join('-')}
        onPreviewYearChange={setPreviewYear}
      />
    </div>
  );
};

export default FiscalPeriodsEditor;
